import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeTable } from '../lib/realtime';
import type { Trip } from '../lib/types';

interface UseDriverTripsResult {
  trips: Trip[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDriverTrips(driverId: string | undefined): UseDriverTripsResult {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!driverId) return;
    setError(null);
    const { data, error: err } = await supabase
      .from('trips')
      .select('*')
      .eq('driver_id', driverId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (err) {
      setError(err.message);
      setTrips([]);
    } else {
      setTrips((data as Trip[]) ?? []);
    }
    setLoading(false);
  }, [driverId]);

  useEffect(() => {
    if (!driverId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    refetch();
    const unsub = subscribeTable('trips', refetch, `driver_id=eq.${driverId}`);
    return unsub;
  }, [driverId, refetch]);

  return { trips, loading, error, refetch };
}
